/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridContainer from '../../components/Grid/GridContainer';
import { SimCardServiceTemplate } from '../../interfaces/platform/SimCardServiceTemplate';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import GridItem from '../../components/Grid/GridItem';
import CustomInputExt from '../../components/CustomInput/CustomInputExt';
import CardHeaderExt from '../../components/Heading/CardHeaderExt';
import { Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import I18n from '../../components/Translate/I18n';
import CardBody from '../../components/Card/CardBody';
import Card from '../../components/Card/Card';
import { ServicesNotActiveList } from './ServicesNotActiveList';
import { FeatureTypes, FeatureVerbType } from '../../modules/features/FeaturesTypes';
import { ServiceTempateElementList } from './ServiceTempateElementList';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import { ComponentParam } from './component/ComponentByGroup';

const _ = require('underscore');

type ServiceTemplateEditType = {
    handleSetBrandText: (text: string) => void;
};

export type ServiceTemplateItems = {
    components: Array<{ [key: string]: { verb: string; param: Array<{ name: string; value: string | number }> } }>;
    promotions?: Array<{ value: number; name: string }> | null;
};

export const ServiceTemplateEdit: FunctionComponent<ServiceTemplateEditType> = ({ handleSetBrandText }): ReactElement => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [serviceTemplate, setServiceTemplate] = useState<SimCardServiceTemplate | null>(null);
    const [templateItems, setTemplateItems] = useState<ServiceTemplateItems>({ components: [] });
    const [templateName, setTemplateName] = useState('');
    const [templateActive, setTemplateActive] = useState<boolean>(false);

    const listType = new FeatureTypes(process.env);

    useEffect(() => {
        if (serviceTemplate === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getWithCallback(
                FormGeneralHelper.getByUri('/' + id),
                'sim_card_service_edit_item',
                (data: SimCardServiceTemplate) => {
                    setTemplateItems(JSON.parse(data.templateItems));
                    setTemplateName(data.templateName);
                    setServiceTemplate(data);
                    setTemplateActive(data.active);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [dispatch, id, serviceTemplate]);

    const saveCallback = (): void => {
        if (serviceTemplate) {
            serviceTemplate.templateItems = JSON.stringify(templateItems);
            serviceTemplate.templateName = templateName;
            serviceTemplate.active = templateActive;
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.putWithCallback(
                FormGeneralHelper.getByUri(serviceTemplate['@id']),
                'sim_card_service_update_template_items',
                serviceTemplate,
                (data: SimCardServiceTemplate) => {
                    setTemplateItems(JSON.parse(data.templateItems));
                    setTemplateName(data.templateName);
                    setServiceTemplate(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    };

    const deleteCallbck = (serviceName: string): void => {
        const newItems: Array<{ [key: string]: { verb: string; param: Array<{ name: string; value: string | number }> } }> = [];
        for (let i = 0; i < templateItems.components.length; i++) {
            const key = Object.keys(templateItems.components[i])[0];
            const values = templateItems.components[i][key];
            if (key !== serviceName) {
                newItems.push({ [key]: values });
            }
        }
        setTemplateItems({ components: newItems });
    };

    const addCallback = (componentParams: ComponentParam): void => {
        const newItems = _.clone(templateItems);
        let params: any = [];
        try {
            params = JSON.parse(componentParams.params);
        } catch (e) {
            console.log(e);
        }
        newItems.components.push({
            [componentParams.componentName]: {
                verb: componentParams.status === 1 ? FeatureVerbType.ACTIVATE : FeatureVerbType.DEACTIVATE,
                param: params,
            },
        });
        setTemplateItems(newItems);
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        setTemplateName(value);
    };

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        const checked = event.target.checked;
        setTemplateActive(checked);
    };

    return (
        <GridContainer>
            <GridItem xs={12}>
                <Card>
                    <CardHeaderExt
                        additionalContent={
                            <React.Fragment>
                                <I18n trans="prov:features.edit_template" />
                            </React.Fragment>
                        }
                    />
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={12}>
                                {serviceTemplate && (
                                    <Typography variant="subtitle2">
                                        <I18n trans="prov:features.template_id" valueObject={{ id: serviceTemplate.id }} />
                                    </Typography>
                                )}
                            </GridItem>
                            <GridItem xs={6}>
                                <CustomInputExt
                                    formControlProps={{ fullWidth: true }}
                                    required={false}
                                    labelText="prov:features.template_name"
                                    name="templateName"
                                    value={templateName}
                                    onChange={onChangeEvent}
                                />
                            </GridItem>
                            <GridItem xs={6} />
                            <GridItem xs={6}>
                                <FormControlLabel
                                    control={<Checkbox checked={templateActive} name="template_active" onChange={onCheckBoxEvent} />}
                                    label={<I18n trans="prov:features.template_active" />}
                                />
                            </GridItem>
                            {serviceTemplate && (
                                <React.Fragment>
                                    <GridItem xs={12}>
                                        <Typography variant="subtitle2">
                                            <I18n trans="prov:features.template_positions" />
                                        </Typography>
                                    </GridItem>
                                    <GridItem xs={6}>
                                        <ServiceTempateElementList serviceTemplate={templateItems} deleteCallback={deleteCallbck} />
                                    </GridItem>
                                    <GridItem xs={6}>
                                        <ServicesNotActiveList operatorTypes={listType.operatorType} addCallback={addCallback} />
                                    </GridItem>
                                </React.Fragment>
                            )}
                        </GridContainer>
                        <GridContainer>
                            <GridItem xs={12}>
                                <CustomTextButtom callback={saveCallback} callbackParam={{}} label="general:common.save" size="sm" />
                            </GridItem>
                        </GridContainer>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};
